import { useMemo, useState } from "react";
import type { PublicDocumentTag, DocumentTagNamespace } from "@vedaunion/shared";
import { useDocumentTags, useCreateDocumentTag } from "../../lib/documents";
import { useCan } from "../../lib/auth";

interface TagPickerProps {
  selectedIds: number[];
  onChange: (next: number[]) => void;
}

/**
 * Three-namespace tag selector. Renders all `type` and `deity` tags as
 * pill toggles grouped by namespace; `topic` tags can be created inline by
 * authors with `documents.manage_tags` (so admins don't need to bounce to a
 * separate screen for free-form topics like "Sri Rudram", "winter solstice").
 *
 * Visual register: vellum chips with a violet active state, mirroring the
 * filter chips on /dashboard/articles.
 */
export function TagPicker({ selectedIds, onChange }: TagPickerProps) {
  const tagsQ = useDocumentTags();
  const tags = tagsQ.data?.tags ?? [];
  const canManage = useCan("documents.manage_tags");
  const createTag = useCreateDocumentTag();

  const [topicDraft, setTopicDraft] = useState("");
  const [topicError, setTopicError] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const map: Record<DocumentTagNamespace, PublicDocumentTag[]> = {
      type: [],
      deity: [],
      topic: [],
    };
    for (const t of tags) map[t.namespace].push(t);
    return map;
  }, [tags]);

  const selected = new Set(selectedIds);

  const toggle = (id: number) => {
    if (selected.has(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const addTopic = async () => {
    const label = topicDraft.trim();
    if (!label) return;
    setTopicError(null);
    const slug = label
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "") // strip combining diacritics for the slug
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64);
    if (!slug) {
      setTopicError("Tag label must contain at least one ASCII character.");
      return;
    }
    try {
      const out = await createTag.mutateAsync({
        namespace: "topic",
        slug,
        label,
        description: null,
        sortOrder: 0,
      });
      setTopicDraft("");
      onChange([...selectedIds, out.tag.id]);
    } catch (err) {
      setTopicError("Failed to create tag.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <NamespaceGroup
        title="Type"
        hint="What this document is — multiple are fine for hybrids (e.g. mantra + stotra)."
        tags={grouped.type}
        selected={selected}
        onToggle={toggle}
      />
      <NamespaceGroup
        title="Deity"
        hint="Which figure or principle the document concerns."
        tags={grouped.deity}
        selected={selected}
        onToggle={toggle}
      />
      <div className="flex flex-col gap-2">
        <NamespaceGroup
          title="Topic"
          hint="Free-form themes — collection names, occasions, special threads."
          tags={grouped.topic}
          selected={selected}
          onToggle={toggle}
          emptyText={canManage ? "No topic tags yet — add one below." : "No topic tags yet."}
        />
        {canManage && (
          <div className="flex flex-wrap gap-2 mt-1">
            <input
              type="text"
              className="auth-input flex-1 min-w-[200px]"
              placeholder="Add a topic tag (e.g. winter solstice)"
              value={topicDraft}
              maxLength={100}
              onChange={(e) => setTopicDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void addTopic();
                }
              }}
            />
            <button
              type="button"
              className="btn-app-ghost is-small"
              onClick={() => void addTopic()}
              disabled={createTag.isPending || !topicDraft.trim()}
            >
              {createTag.isPending ? "Adding…" : "Add topic"}
            </button>
          </div>
        )}
        {topicError && (
          <p className="auth-hint" style={{ color: "var(--color-danger)" }}>
            {topicError}
          </p>
        )}
      </div>
    </div>
  );
}

function NamespaceGroup({
  title,
  hint,
  tags,
  selected,
  onToggle,
  emptyText,
}: {
  title: string;
  hint: string;
  tags: PublicDocumentTag[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  emptyText?: string;
}) {
  return (
    <div>
      <p className="auth-label" style={{ marginBottom: 4 }}>
        {title}
      </p>
      <p
        className="font-grotesk text-[0.7rem] tracking-[0.04em] text-[var(--color-ink-mute)] mb-2"
      >
        {hint}
      </p>
      {tags.length === 0 ? (
        <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-mute)] italic">
          {emptyText ?? "No tags."}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {tags.map((t) => {
            const active = selected.has(t.id);
            return (
              <li key={t.id}>
                <button
                  type="button"
                  className={`chip ${active ? "is-active" : ""}`}
                  onClick={() => onToggle(t.id)}
                  aria-pressed={active}
                  title={t.description ?? undefined}
                >
                  {t.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
